// FulfillmentTracker.js

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiRequest } from '../utils/api';
import {
  STATUS_OPTIONS, STATUS_STYLES, STATUS_VARIANTS, BROKEN_ITEM_FLOW,
  LM_BOX_STATUS_OPTIONS, LM_BOX_STATUS_STYLES,
} from '../utils/constants';
import { getCachedRecords, setCachedRecords } from '../utils/recordsCache';
import { exportCSV } from '../utils/exportCSV';
import {
  Spinner, Button, Badge, Toggle, PageHeader, EmptyState,
  TableHeader, RecordRow, SkeletonRow, Pagination,
} from './ui';

const SKELETON_COLS = [80, 120, 140, 88, 160, 120, 88, 120, 60, 60, 144];

const CSV_COLUMNS = [
  'TransactionID', 'GiftDate', 'ConstituentID', 'FirstName', 'LastName',
  'SourceCode', 'SourceCodeDescription', 'PromotionName', 'TotalAmount',
  'Status', 'TransactionTag', 'IsNewMembership', 'AssignedTo',
  'LetterMade', 'TEDInteractionLogged', 'LifeMemberBoxStatus',
  'StaffNotes', 'MembershipType', 'MembershipStartDate',
  'MembershipEndDate', 'Address', 'City', 'State', 'StateAbbreviation',
  'ZipCode', 'Country', 'ReceiptText', 'RecipientEmail', 'MembershipComment',
  'GiftRecipientName', 'GiftRecipientContact',
];

// Column definitions for TableHeader
const COLUMNS = [
  { label: 'Gift Date',      field: 'GiftDate',        className: 'w-24' },
  { label: 'Constituent ID', field: 'ConstituentID',   className: 'w-32' },
  { label: 'Name',           field: 'LastName',         className: 'w-36' },
  { label: 'Source Code',    field: 'SourceCode',       className: 'w-24' },
  { label: 'Promotion',      field: 'PromotionName',    className: 'w-40' },
  { label: 'Tag',            field: 'TransactionTag',   className: 'w-36' },
  { label: 'Amount',         field: 'TotalAmount',      className: 'w-24' },
  { label: 'Status',         field: 'Status',           className: 'w-32', title: 'Promotional Item Status' },
  { label: 'Letter',         field: null,               className: 'w-16' },
  { label: 'Interaction',    field: null,               className: 'w-16' },
  { label: 'Notes',          field: null,               className: 'w-36' },
];

// Valid TransactionTag values for the editable dropdown.
// Data Check is a Status value, not a tag — intentionally excluded.
const TRANSACTION_TAGS = [
  'Annual',
  'Life',
  'Gifted Annual',
  'Gifted Life',
  'Gifted Life Benefactor',
  'Gifted Life Recipient',
  'Gift',
  'Cvent',
];

// const ASSIGNED_TO_OPTIONS = ['Unassigned', 'Jackie', 'Allison']; // hidden until Cognito group lookup is implemented

const filterSelectClass = [
  'border border-[var(--color-border)] rounded-[var(--radius-lg)]',
  'px-3 h-9 text-sm text-[var(--color-text-primary)]',
  'focus:outline-none focus:ring-1 focus:ring-[var(--color-orange)] focus:border-[var(--color-orange)]',
  'bg-white cursor-pointer',
].join(' ');

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

// Re-boxed and Resent only make sense once a record has already entered
// the broken-item flow. Everywhere else, offer the full list minus those two.
function getAvailableStatuses(currentStatus) {
  if (BROKEN_ITEM_FLOW.includes(currentStatus)) return STATUS_OPTIONS;
  return STATUS_OPTIONS.filter(s => s !== 'Re-boxed' && s !== 'Resent');
}

// ---------------------------------------------------------------------------
// Date normalization
// ---------------------------------------------------------------------------
function normalizeDateField(raw) {
  if (!raw || !raw.includes('/')) return raw;
  const [m, d, y] = raw.split('/');
  if (!m || !d || !y) return raw;
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function normalizeRecord(r) {
  return {
    ...r,
    GiftDate:            normalizeDateField(r.GiftDate),
    MembershipStartDate: normalizeDateField(r.MembershipStartDate),
    MembershipEndDate:   normalizeDateField(r.MembershipEndDate),
  };
}

function formatDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
}

// ---------------------------------------------------------------------------
// TransactionTag helpers
// ---------------------------------------------------------------------------

// Returns inline style for a tag pill.
// Annual  → Illini Blue (--color-info)
// Life    → Illini Orange (--color-orange)
// Gifted* → Teal (--color-teal)
// Cvent   → Brown (--color-brown)
// Gift / null → Neutral gray
function getTagStyle(tag) {
  if (!tag || tag === 'Gift') {
    return {
      background: 'var(--color-surface-alt)',
      color:      'var(--color-text-secondary)',
      border:     '1px solid var(--color-border)',
    };
  }
  if (tag === 'Annual') {
    return {
      background: 'var(--color-info-bg)',
      color:      'var(--color-info)',
      border:     '1px solid var(--color-info-border)',
    };
  }
  if (tag === 'Life') {
    return {
      background: 'var(--color-orange-light)',
      color:      'var(--color-orange)',
      border:     '1px solid var(--color-orange-muted)',
    };
  }
  if (tag.startsWith('Gifted')) {
    return {
      background: 'var(--color-teal-bg)',
      color:      'var(--color-teal)',
      border:     '1px solid var(--color-teal-border)',
    };
  }
  if (tag === 'Cvent') {
    return {
      background: 'var(--color-brown-bg)',
      color:      'var(--color-brown)',
      border:     '1px solid var(--color-brown-border)',
    };
  }
  return {
    background: 'var(--color-surface-alt)',
    color:      'var(--color-text-secondary)',
    border:     '1px solid var(--color-border)',
  };
}

// True if this tag is any of the Gifted variants.
function isGiftedTag(tag) {
  return tag && tag.startsWith('Gifted');
}

// True if this tag is any Life-family variant (Life, Gifted Life,
// Gifted Life Benefactor, Gifted Life Recipient). Controls visibility
// of the Life Member Box icon + DetailsPanel dropdown.
function isLifeTag(tag) {
  return Boolean(tag) && tag.includes('Life');
}

// ---------------------------------------------------------------------------
// LetterIcon
// Grayed file = no letter. Green file = ready. Red diagonal = failed.
// ---------------------------------------------------------------------------
function LetterIcon({ status }) {
  if (!status) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
        stroke="var(--color-border-strong)" className="w-4 h-4" title="No letter">
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      </svg>
    );
  }
  if (status === 'generating') return <Spinner size="sm" />;
  if (status === 'ready') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
        stroke="var(--color-success)" className="w-4 h-4" title="Letter ready">
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      </svg>
    );
  }
  if (status === 'failed') {
    return (
      <span title="Letter generation failed" className="relative inline-flex">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
          stroke="var(--color-border-strong)" className="w-4 h-4">
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
        <svg className="absolute inset-0 w-4 h-4" viewBox="0 0 24 24">
          <line x1="4" y1="4" x2="20" y2="20" stroke="var(--color-error)" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// LifeBoxIcon
// Small colored box icon reflecting LifeMemberBoxStatus. Same visual
// language as LetterIcon — muted when idle, colored once something's
// happened. Colors come from LM_BOX_STATUS_STYLES (reused design tokens,
// not new ones): gray = Pending, yellow = Hold, blue = Boxed, green = Sent.
// ---------------------------------------------------------------------------
function LifeBoxIcon({ status }) {
  const key   = status || 'Pending';
  const style = LM_BOX_STATUS_STYLES[key] || LM_BOX_STATUS_STYLES.Pending;
  return (
    <span
      title={`Life Member Box: ${key}`}
      className="inline-flex items-center justify-center w-4 h-4 rounded-[3px] shrink-0"
      style={{ background: style.background, border: `1px solid ${style.borderColor}` }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.75}
        stroke={style.color} className="w-2.5 h-2.5">
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M20.25 7.5l-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
      </svg>
    </span>
  );
}

// ── SummaryBar ────────────────────────────────────────────────────────────────
function SummaryBar({ records }) {
  const counts = STATUS_OPTIONS.reduce(
    (acc, s) => ({ ...acc, [s]: records.filter(r => (r.Status || 'Pending') === s).length }),
    {}
  );
  const pills = [
    { label: 'Total', value: records.length, variant: 'default' },
    ...STATUS_OPTIONS.map(s => ({ label: s, value: counts[s], variant: STATUS_VARIANTS[s] || 'default' })),
  ];
  return (
    <div className="flex flex-wrap gap-2 shrink-0">
      {pills.map(({ label, value, variant }) => (
        <Badge key={label} variant={variant}>
          <span className="font-bold text-sm">{value}</span>
          <span className="font-normal opacity-75">{label}</span>
        </Badge>
      ))}
    </div>
  );
}

// ── GiftRecipientLookup ───────────────────────────────────────────────────────
// Shown inside MembershipSection when tag is a Gifted variant.
// Auto-fires quick-match on GiftRecipientContact.
function GiftRecipientLookup({ name, contact }) {
  const [matchState, setMatchState] = useState('idle');
  const [match,      setMatch]      = useState(null);

  useEffect(() => {
    if (!contact) return;
    setMatchState('loading');
    setMatch(null);
    apiRequest('/id-matching/quick-match', {
      method: 'POST',
      body: JSON.stringify({ email: contact }),
    })
      .then(data => {
        if (data.status === 'matched') { setMatch(data); setMatchState('matched'); }
        else setMatchState('not_found');
      })
      .catch(() => setMatchState('error'));
  }, [contact]);

  return (
    <div className="space-y-1">
      <div className="text-xs text-[var(--color-text-secondary)]">
        <span className="text-[var(--color-text-muted)]">Recipient Email: </span>{contact || '—'}
      </div>
      <div className="text-xs text-[var(--color-text-secondary)]">
        <span className="text-[var(--color-text-muted)]">Recipient Name: </span>{name || '—'}
      </div>
      <div className="text-xs text-[var(--color-text-secondary)] flex items-center gap-1.5">
        <span className="text-[var(--color-text-muted)]">Recipient ID: </span>
        {!contact && <span className="italic text-[var(--color-text-muted)]">No email to look up</span>}
        {contact && matchState === 'loading'   && <Spinner size="sm" />}
        {contact && matchState === 'matched'   && <span className="font-medium font-mono text-[var(--color-text-primary)]">{match.aux_id}</span>}
        {contact && matchState === 'not_found' && <span className="italic text-[var(--color-text-muted)]">No matching ID found</span>}
        {contact && matchState === 'error'     && <span className="italic text-[var(--color-error)]">No matching ID found</span>}
      </div>
    </div>
  );
}

// ── MembershipSection ─────────────────────────────────────────────────────────
// Always shows Type/Start/End/Comment regardless of tag.
// Gifted variants additionally show recipient lookup below the standard fields.
function MembershipSection({ record }) {
  const tag      = record.TransactionTag || 'Gift';
  const tagStyle = getTagStyle(tag);

  return (
    <div>
      <p className="text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-2">
        Membership
      </p>

      {/* Tag pill + New indicator */}
      <div className="mb-2">
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
          style={tagStyle}
        >
          {tag}
        </span>
        {record.IsNewMembership === true && (
          <span className="ml-1.5 text-[10px] font-semibold text-[var(--color-success)]">New</span>
        )}
      </div>

      {/* Always show membership fields — whatever came with the record */}
      <div className="space-y-1">
        {[
          ['Type',    record.MembershipType              || '—'],
          ['Start',   formatDate(record.MembershipStartDate)],
          ['End',     formatDate(record.MembershipEndDate)],
          ['Comment', record.MembershipComment           || '—'],
        ].map(([k, v]) => (
          <div key={k} className="text-xs text-[var(--color-text-secondary)]">
            <span className="text-[var(--color-text-muted)]">{k}: </span>{v}
          </div>
        ))}
      </div>

      {/* Additional recipient details for Gifted variants */}
      {isGiftedTag(tag) && (
        <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
          <p className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider mb-1.5">Gift Recipient</p>
          <GiftRecipientLookup
            name={record.GiftRecipientName}
            contact={record.GiftRecipientContact}
          />
        </div>
      )}
    </div>
  );
}

// ── DetailsPanel ──────────────────────────────────────────────────────────────
function DetailsPanel({
  record,
  notes, onNotesChange, onNotesSave, saveState,
  notesRef, focusNotes,
  transactionTag, onTagChange,
  lifeMemberBoxStatus, onLifeBoxChange,
}) {
  const splits = record.Splits || [];

  useEffect(() => {
    if (focusNotes && notesRef?.current) notesRef.current.focus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fieldClass    = 'text-xs text-[var(--color-text-secondary)]';
  const labelClass    = 'text-[var(--color-text-muted)]';
  const dropdownClass = 'text-xs border border-[var(--color-border)] rounded-[var(--radius-md)] px-2 py-1.5 w-full focus:outline-none focus:ring-1 focus:ring-[var(--color-orange)] bg-white text-[var(--color-text-primary)] cursor-pointer';
  const microLabel    = 'text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider block mb-1';

  return (
    <div className="px-5 py-4 bg-[var(--color-surface)] space-y-5">

      {/* ── Row 1: Membership · Address · Fund Splits ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

        {/* 1. Membership */}
        <MembershipSection record={{ ...record, TransactionTag: transactionTag }} />

        {/* 2. Address */}
        <div>
          <p className="text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-2">Address</p>
          <div className="space-y-1">
            {[
              ['Street',          record.Address || '—'],
              ['City',            record.City    || '—'],
              ['State',           [record.State, record.StateAbbreviation].filter(Boolean).join(' / ') || '—'],
              ['Zip',             record.ZipCode || '—'],
              ['Country',         record.Country || '—'],
              ['Purchaser Email', record.RecipientEmail || '—'],
            ].map(([k, v]) => (
              <div key={k} className={fieldClass}><span className={labelClass}>{k}: </span>{v}</div>
            ))}
          </div>
        </div>

        {/* 3. Fund Splits */}
        <div>
          <p className="text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-2">Fund Splits</p>
          {splits.length === 0 ? (
            <p className="text-xs text-[var(--color-text-muted)]">No split data</p>
          ) : (
            <div className="space-y-1">
              {splits.map((s, i) => (
                <div key={i} className={fieldClass}>
                  <div className="flex justify-between">
                    <span className="truncate mr-4">{s.Designation}</span>
                    <span className="shrink-0 font-medium">${parseFloat(s.Amount).toFixed(2)}</span>
                  </div>
                  {s.DesignationLookupID && (
                    <div className="text-[var(--color-text-muted)] font-mono">{s.DesignationLookupID}</div>
                  )}
                </div>
              ))}
              <div className="flex justify-between text-xs font-semibold text-[var(--color-text-primary)] border-t border-[var(--color-border)] pt-1 mt-1">
                <span>Total</span>
                <span>${parseFloat(record.TotalAmount || 0).toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Row 2: Letter · Fulfillment ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-[var(--color-border)]">

        {/* 4. Letter */}
        <div>
          <p className="text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-3">Letter</p>
          <div className="space-y-3">

            {/* Status + download */}
            <div className="flex items-center gap-2">
              <LetterIcon status={record.LetterStatus} />
              <span className={`text-xs ${
                record.LetterStatus === 'ready'  ? 'text-[var(--color-success)]' :
                record.LetterStatus === 'failed' ? 'text-[var(--color-error)]'   :
                'text-[var(--color-text-muted)]'
              }`}>
                {!record.LetterStatus                 && 'No letter generated'}
                {record.LetterStatus === 'generating' && 'Generating…'}
                {record.LetterStatus === 'ready'      && 'Letter ready'}
                {record.LetterStatus === 'failed'     && (record.LetterError || 'Generation failed')}
              </span>
              {record.LetterStatus === 'ready' && record.LetterS3Key && (
                <button className="text-xs text-[var(--color-info)] hover:underline ml-1">
                  Download .docx
                </button>
              )}
            </div>

            {/* TransactionTag dropdown — editable, triggers letter regen via DynamoDB stream */}
            <div>
              <label className={microLabel}>Transaction Tag</label>
              <select
                value={transactionTag || 'Gift'}
                onChange={e => onTagChange(e.target.value)}
                className={dropdownClass}
              >
                {TRANSACTION_TAGS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <p className="mt-1 text-[10px] text-[var(--color-text-muted)]">
                Override if tag was misclassified. Triggers letter regeneration.
              </p>
            </div>

            {/* IsNewMembership — read-only */}
            <div className={fieldClass}>
              <span className={labelClass}>New Membership: </span>
              {record.IsNewMembership === true  && <span className="text-[var(--color-success)] font-medium">Yes</span>}
              {record.IsNewMembership === false && <span className="text-[var(--color-text-muted)]">No (renewal or non-membership)</span>}
              {record.IsNewMembership == null   && <span className="italic text-[var(--color-text-muted)]">Not yet determined</span>}
            </div>

          </div>
        </div>

        {/* 5. Fulfillment */}
        <div>
          <p className="text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-3">Fulfillment</p>
          <div className="space-y-3">

            {/* AssignedTo — hidden until Cognito group lookup is implemented */}
            {/* <div>
              <label className={microLabel}>Assigned To</label>
              <select
                value={assignedTo || 'Unassigned'}
                onChange={e => onAssignedChange(e.target.value)}
                className={dropdownClass}
              >
                {ASSIGNED_TO_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div> */}

            {/* Life Member Box status — only for Life-family tags */}
            {isLifeTag(transactionTag) && (
              <div>
                <label className={microLabel}>Life Member Box</label>
                <select
                  value={lifeMemberBoxStatus || 'Pending'}
                  onChange={e => onLifeBoxChange(e.target.value)}
                  className={dropdownClass}
                >
                  {LM_BOX_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}

            {/* Staff Notes — save on blur */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={microLabel}>Staff Notes</label>
                {saveState === 'saving' && <span className="flex items-center gap-1 text-xs text-[var(--color-text-muted)]"><Spinner size="sm" /> Saving...</span>}
                {saveState === 'saved'  && <span className="text-xs text-[var(--color-success)]">✓ Saved</span>}
                {saveState === 'error'  && <span className="text-xs text-[var(--color-error)]">Save failed</span>}
              </div>
              <textarea
                ref={notesRef}
                value={notes}
                onChange={e => onNotesChange(e.target.value)}
                onBlur={onNotesSave}
                rows={4}
                placeholder="Add notes about this record..."
                className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-[var(--radius-md)]
                           text-[var(--color-text-primary)] focus:outline-none focus:ring-1
                           focus:ring-[var(--color-orange)] focus:border-[var(--color-orange)]
                           bg-white placeholder-[var(--color-text-muted)] resize-none leading-relaxed"
              />
            </div>

          </div>
        </div>
      </div>

    </div>
  );
}

// ── FulfillmentRecordRow ──────────────────────────────────────────────────────
function FulfillmentRecordRow({ record, selected, onToggle, onRefresh }) {
  const [notes,                setNotes]                = useState(record.StaffNotes            || '');
  const [status,                setStatus]               = useState(record.Status                || 'Pending');
  const [transactionTag,        setTransactionTag]       = useState(record.TransactionTag         || 'Gift');
  const [letterMade,            setLetterMade]           = useState(Boolean(record.LetterMade));
  const [tedInteractionLogged,  setTedInteractionLogged] = useState(Boolean(record.TEDInteractionLogged));
  const [lifeMemberBoxStatus,   setLifeMemberBoxStatus]  = useState(record.LifeMemberBoxStatus    || 'Pending');
  const [saveState,             setSaveState]            = useState(null);
  const [expanded,              setExpanded]             = useState(false);
  const [focusNotes,            setFocusNotes]           = useState(false);

  const notesRef     = useRef(null);
  const saveTimerRef = useRef(null);

  useEffect(() => { setNotes(record.StaffNotes                       || ''); },      [record.StaffNotes]);
  useEffect(() => { setStatus(record.Status                          || 'Pending'); }, [record.Status]);
  useEffect(() => { setTransactionTag(record.TransactionTag          || 'Gift'); },   [record.TransactionTag]);
  useEffect(() => { setLetterMade(Boolean(record.LetterMade)); },                     [record.LetterMade]);
  useEffect(() => { setTedInteractionLogged(Boolean(record.TEDInteractionLogged)); }, [record.TEDInteractionLogged]);
  useEffect(() => { setLifeMemberBoxStatus(record.LifeMemberBoxStatus || 'Pending'); }, [record.LifeMemberBoxStatus]);

  async function patch(payload) {
    setSaveState('saving');
    try {
      await apiRequest(`/fulfillment/records/${record.TransactionID}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      setSaveState('saved');
      saveTimerRef.current = setTimeout(() => setSaveState(null), 2000);
      onRefresh(true);
    } catch {
      setSaveState('error');
      saveTimerRef.current = setTimeout(() => setSaveState(null), 3000);
    }
  }

  // Every PATCH must carry every editable field, or a save from one
  // control (e.g. Notes) would blow away another's current value
  // (e.g. LifeMemberBoxStatus) since the API does a full field write.
  // This is the one place that assembles that payload from current
  // local state, with `overrides` layering in whatever just changed.
  function currentPayload(overrides = {}) {
    return {
      Status:               status,
      StaffNotes:            notes,
      TransactionTag:         transactionTag,
      AssignedTo:             record.AssignedTo || 'Unassigned',
      LetterMade:             letterMade,
      TEDInteractionLogged:   tedInteractionLogged,
      LifeMemberBoxStatus:    lifeMemberBoxStatus,
      ...overrides,
    };
  }

  function handleStatusChange(val) {
    setStatus(val);
    patch(currentPayload({ Status: val }));
  }
  function handleTagChange(val) {
    setTransactionTag(val);
    patch(currentPayload({ TransactionTag: val }));
  }
  function handleNotesSave() {
    if (notes !== (record.StaffNotes || '')) patch(currentPayload());
  }
  function handleLetterMadeChange(val) {
    setLetterMade(val);
    patch(currentPayload({ LetterMade: val }));
  }
  function handleInteractionChange(val) {
    setTedInteractionLogged(val);
    patch(currentPayload({ TEDInteractionLogged: val }));
  }
  function handleLifeBoxChange(val) {
    setLifeMemberBoxStatus(val);
    patch(currentPayload({ LifeMemberBoxStatus: val }));
  }

  useEffect(() => () => clearTimeout(saveTimerRef.current), []);

  function handleNoteClick() {
    if (!expanded) {
      setFocusNotes(true);
      setExpanded(true);
    } else {
      notesRef.current?.focus();
    }
  }

  function handleExpandedChange(val) {
    setExpanded(val);
    if (!val) setFocusNotes(false);
  }

  const cellClass   = 'px-3 py-2.5 text-xs text-[var(--color-text-secondary)] align-top';
  const statusStyle = STATUS_STYLES[status] || {};
  const tag         = transactionTag || 'Gift';
  const tagStyle    = getTagStyle(tag);

  return (
    <RecordRow
      id={record.TransactionID}
      selected={selected}
      onToggle={onToggle}
      colSpan={13}
      expanded={expanded}
      onExpandedChange={handleExpandedChange}
      expandedContent={
        <DetailsPanel
          record={record}
          notes={notes}
          onNotesChange={setNotes}
          onNotesSave={handleNotesSave}
          saveState={saveState}
          notesRef={notesRef}
          focusNotes={focusNotes}
          transactionTag={transactionTag}
          onTagChange={handleTagChange}
          lifeMemberBoxStatus={lifeMemberBoxStatus}
          onLifeBoxChange={handleLifeBoxChange}
        />
      }
    >
      {/* Gift Date */}
      <td className={`${cellClass} w-24 text-[var(--color-text-primary)]`}>{formatDate(record.GiftDate)}</td>

      {/* Constituent ID */}
      <td className={`${cellClass} w-32 font-mono`}>{record.ConstituentID || '—'}</td>

      {/* Name */}
      <td className={`${cellClass} w-36 font-medium text-[var(--color-text-primary)]`}>
        {record.FirstName || ''} {record.LastName || ''}
      </td>

      {/* Source Code */}
      <td className={`${cellClass} w-24 font-mono`} title={record.SourceCode}>
        {record.SourceCode
          ? record.SourceCode.length > 8 ? record.SourceCode.slice(0, 8) + '…' : record.SourceCode
          : '—'}
      </td>

      {/* Promotion */}
      <td className={`${cellClass} w-40`}>{record.PromotionName || '—'}</td>

      {/* Transaction Tag — colored pill + Life Member Box indicator */}
      <td className={`${cellClass} w-36`}>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
            style={tagStyle}
          >
            {tag}
          </span>
          {isLifeTag(tag) && <LifeBoxIcon status={lifeMemberBoxStatus} />}
        </span>
      </td>

      {/* Amount */}
      <td className={`${cellClass} w-24 font-medium text-[var(--color-text-primary)]`}>
        ${parseFloat(record.TotalAmount || 0).toFixed(2)}
      </td>

      {/* Status — inline dropdown, gated to hide Re-boxed/Resent until reachable */}
      <td className="px-3 py-2 w-32 align-top">
        <select
          value={status}
          onChange={e => handleStatusChange(e.target.value)}
          className="text-xs border rounded-[var(--radius-md)] px-2 py-1.5 w-full font-semibold focus:outline-none focus:ring-1 focus:ring-[var(--color-orange)] cursor-pointer"
          style={statusStyle}
        >
          {getAvailableStatuses(status).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>

      {/* Letter Made — manual staff checkbox, independent of automated LetterStatus */}
      <td className="px-3 py-2 w-16 align-top text-center">
        <input
          type="checkbox"
          checked={letterMade}
          onChange={e => handleLetterMadeChange(e.target.checked)}
          className="w-3.5 h-3.5 rounded border-[var(--color-border)] accent-[var(--color-orange)] cursor-pointer"
        />
      </td>

      {/* Interaction Logged — whether this gift was logged as an interaction in TED */}
      <td className="px-3 py-2 w-16 align-top text-center">
        <input
          type="checkbox"
          checked={tedInteractionLogged}
          onChange={e => handleInteractionChange(e.target.checked)}
          className="w-3.5 h-3.5 rounded border-[var(--color-border)] accent-[var(--color-orange)] cursor-pointer"
        />
      </td>

      {/* Assigned To — hidden until Cognito group lookup is implemented */}
      {/* <td className={`${cellClass} w-28`}>
        {assignedTo && assignedTo !== 'Unassigned'
          ? <span className="font-medium text-[var(--color-text-primary)]">{assignedTo}</span>
          : <span className="text-[var(--color-text-muted)] italic">Unassigned</span>}
      </td> */}

      {/* Notes */}
      <td className={`${cellClass} w-36 align-top`}>
        {notes ? (
          <span
            className="block truncate cursor-pointer hover:text-[var(--color-text-primary)] transition-colors"
            title={notes}
          >
            {notes}
          </span>
        ) : (
          <span
            onClick={handleNoteClick}
            className="text-[var(--color-text-muted)] italic cursor-pointer hover:text-[var(--color-text-primary)] transition-colors"
          >
            Add note...
          </span>
        )}
      </td>
    </RecordRow>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function FulfillmentTracker() {
  const [records,        setRecords]        = useState(() => getCachedRecords() || []);
  const [loading,        setLoading]        = useState(() => !getCachedRecords());
  const [search,         setSearch]         = useState('');
  const [filterStatus,   setFilterStatus]   = useState('All');
  const [filterPromo,    setFilterPromo]    = useState('All');
  const [filterSC,       setFilterSC]       = useState('All');
  const [membershipOnly, setMembershipOnly] = useState(true);
  const [sortField,      setSortField]      = useState('GiftDate');
  const [sortDir,        setSortDir]        = useState('desc');
  const [page,           setPage]           = useState(1);
  const [pageSize,       setPageSize]       = useState(25);
  const [selectedIds,    setSelectedIds]    = useState(new Set());
  const [allSelected,    setAllSelected]    = useState(false);
  const [bulkProgress,   setBulkProgress]   = useState(null);

  const fetchRecords = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const path = filterStatus !== 'All'
        ? `/fulfillment/records?status=${filterStatus}`
        : `/fulfillment/records`;
      const data = await apiRequest(path);
      const fetched = (data.records || []).map(normalizeRecord);
      setRecords(fetched);
      setCachedRecords(fetched);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filterStatus]);

const didInitialFetch = useRef(false);

useEffect(() => {
  const cached = getCachedRecords();
  if (cached) {
    setRecords(cached.map(normalizeRecord));
    setLoading(false);
  } else {
    fetchRecords();
  }
  didInitialFetch.current = true;
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

useEffect(() => {
  if (!didInitialFetch.current) return;
  fetchRecords(true); // only fires when filterStatus actually changes after mount
}, [fetchRecords]);

  useEffect(() => {
    setSelectedIds(new Set()); setAllSelected(false); setPage(1);
  }, [search, filterStatus, filterPromo, filterSC, membershipOnly]);

  function toggleSort(field) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }

  const promotions  = ['All', ...new Set(records.map(r => r.PromotionName).filter(Boolean))].sort();
  const sourceCodes = ['All', ...new Set(
    records.filter(r => filterPromo === 'All' || r.PromotionName === filterPromo).map(r => r.SourceCode).filter(Boolean)
  )].sort();

  const filtered = records
    .filter(r => {
      const matchSearch = !search ||
        `${r.FirstName} ${r.LastName} ${r.ConstituentID} ${r.TransactionID} ${r.SourceCode} ${r.SourceCodeDescription} ${r.PromotionName}`
          .toLowerCase().includes(search.toLowerCase());
      return (
        matchSearch &&
        (filterPromo  === 'All' || r.PromotionName === filterPromo) &&
        (filterSC     === 'All' || r.SourceCode    === filterSC)    &&
        (filterStatus === 'All' || r.Status        === filterStatus) &&
        (!membershipOnly         || r.IsMembership === true)
      );
    })
    .sort((a, b) => {
      if (sortField === 'GiftDate') {
        const av = a.GiftDate || '', bv = b.GiftDate || '';
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      if (sortField === 'TotalAmount') {
        return sortDir === 'asc'
          ? (parseFloat(a.TotalAmount) || 0) - (parseFloat(b.TotalAmount) || 0)
          : (parseFloat(b.TotalAmount) || 0) - (parseFloat(a.TotalAmount) || 0);
      }
      const av = (a[sortField] || '').toString(), bv = (b[sortField] || '').toString();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });

  const paginated      = filtered.slice((page - 1) * pageSize, page * pageSize);
  const allFilteredIds = new Set(filtered.map(r => r.TransactionID));
  const pageIds        = new Set(paginated.map(r => r.TransactionID));
  const pageAllChecked    = pageIds.size > 0 && [...pageIds].every(id => selectedIds.has(id));
  const pageIndeterminate = !pageAllChecked && [...pageIds].some(id => selectedIds.has(id));

  function toggleOne(id) {
    setAllSelected(false);
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function togglePage() {
    setAllSelected(false);
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (pageAllChecked) pageIds.forEach(id => n.delete(id));
      else                pageIds.forEach(id => n.add(id));
      return n;
    });
  }

  function selectAllFiltered() { setSelectedIds(new Set(allFilteredIds)); setAllSelected(true); }
  function clearSelection()    { setSelectedIds(new Set()); setAllSelected(false); }

  function getTargetRecords() {
    return allSelected ? filtered : records.filter(r => selectedIds.has(r.TransactionID));
  }

  async function runBulkOp(targets, buildPayload) {
    if (targets.length === 0) return;
    const total = targets.length;
    setBulkProgress({ done: 0, total, failed: 0, phase: 'running' });
    let done = 0, failed = 0;
    for (const record of targets) {
      try {
        await apiRequest(`/fulfillment/records/${record.TransactionID}`, {
          method: 'PATCH', body: JSON.stringify(buildPayload(record)),
        });
      } catch { failed += 1; }
      done += 1;
      setBulkProgress({ done, total, failed, phase: 'running' });
    }
    setBulkProgress({ done, total, failed, phase: failed > 0 ? 'error' : 'done' });
    await fetchRecords(true);
    setTimeout(() => { setBulkProgress(null); clearSelection(); }, 2000);
  }

  // Shared fallback payload for bulk operations — every editable field
  // carries its current value forward except whatever this operation
  // is actually changing (passed via `overrides`). Same reasoning as
  // currentPayload() in FulfillmentRecordRow: bulk PATCH is a full
  // field write, so a Status bulk-update must not silently reset
  // LetterMade back to false on every targeted record.
  function buildBulkPayload(record, overrides = {}) {
    return {
      Status:               record.Status               || 'Pending',
      StaffNotes:            record.StaffNotes            || '',
      TransactionTag:         record.TransactionTag         || 'Gift',
      AssignedTo:             record.AssignedTo             || 'Unassigned',
      LetterMade:             Boolean(record.LetterMade),
      TEDInteractionLogged:   Boolean(record.TEDInteractionLogged),
      LifeMemberBoxStatus:    record.LifeMemberBoxStatus    || 'Pending',
      ...overrides,
    };
  }

  function handleBulkStatus(newStatus) {
    runBulkOp(getTargetRecords(), record => buildBulkPayload(record, { Status: newStatus }));
  }

  function handleBulkNotes(appendText) {
    runBulkOp(getTargetRecords(), record => buildBulkPayload(record, {
      StaffNotes: record.StaffNotes ? `${record.StaffNotes}\n${appendText}` : appendText,
    }));
  }

  function handleBulkLetterMade(value) {
    runBulkOp(getTargetRecords(), record => buildBulkPayload(record, { LetterMade: value }));
  }

  function handleBulkInteractionLogged(value) {
    runBulkOp(getTargetRecords(), record => buildBulkPayload(record, { TEDInteractionLogged: value }));
  }

  function handleBulkLifeBoxStatus(value) {
    runBulkOp(getTargetRecords(), record => buildBulkPayload(record, { LifeMemberBoxStatus: value }));
  }

  function handleExportCSV() {
    exportCSV(records, allSelected ? allFilteredIds : selectedIds, CSV_COLUMNS, 'fulfillment_export');
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-white">
      <PageHeader
        title="Fulfillment Tracker"
        subtitle={`${records.length} records · ${new Date().getFullYear()} promotions`}
      />

      <div className="flex flex-col flex-1 min-h-0 p-4 gap-3 bg-[var(--color-surface)]">

        <SummaryBar records={filtered} />

        {/* Filter bar */}
        <div className="flex flex-wrap gap-2 shrink-0 items-center">
          <div className="relative flex-1 min-w-[14rem]">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
              className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] pointer-events-none">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search name, ID, source code, promotion..."
              className="w-full pl-9 pr-3 h-9 border border-[var(--color-border)] rounded-[var(--radius-lg)] text-sm
                         focus:outline-none focus:ring-1 focus:ring-[var(--color-orange)] focus:border-[var(--color-orange)]
                         bg-white text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)]"
            />
          </div>
          <select value={filterPromo} onChange={e => { setFilterPromo(e.target.value); setFilterSC('All'); }} className={`${filterSelectClass} w-48`}>
            {promotions.map(p => <option key={p}>{p}</option>)}
          </select>
          <select value={filterSC} onChange={e => setFilterSC(e.target.value)} className={`${filterSelectClass} w-40`} disabled={filterPromo === 'All'}>
            {sourceCodes.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={`${filterSelectClass} w-36`}>
            <option value="All">All Statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
          </select>
          <Toggle checked={membershipOnly} onChange={setMembershipOnly} label="Membership Only" />
        </div>

        {/* Table */}
        <div className="bg-white rounded-[var(--radius-xl)] border border-[var(--color-border)] flex flex-col flex-1 min-h-0 shadow-[var(--shadow-xs)] overflow-hidden">
          <div className="overflow-auto flex-1">
            {loading ? (
              <table className="w-full text-left table-fixed">
                <thead className="bg-[var(--color-surface-alt)] border-b border-[var(--color-border)] sticky top-0 z-10">
                  <tr>
                    {['', 'Gift Date', 'Constituent ID', 'Name', 'Source Code', 'Promotion', 'Tag', 'Amount', 'Status', 'Letter', 'Interaction', 'Notes', ''].map((h, i) => (
                      <th key={i} className="px-3 py-3 text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 12 }).map((_, i) => <SkeletonRow key={i} columns={SKELETON_COLS} />)}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-left table-fixed">
                <TableHeader
                  columns={COLUMNS}
                  selectedIds={selectedIds}
                  filteredCount={filtered.length}
                  allSelected={allSelected}
                  pageAllChecked={pageAllChecked}
                  pageIndeterminate={pageIndeterminate}
                  onTogglePage={togglePage}
                  onSelectAllFiltered={selectAllFiltered}
                  onClearSelection={clearSelection}
                  onBulkStatus={handleBulkStatus}
                  onBulkLetterMade={handleBulkLetterMade}
                  onBulkInteractionLogged={handleBulkInteractionLogged}
                  onBulkLifeBoxStatus={handleBulkLifeBoxStatus}
                  onBulkNotes={handleBulkNotes}
                  onExportCSV={handleExportCSV}
                  onGenerateLetters={null}
                  bulkProgress={bulkProgress}
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
                <tbody>
                  {paginated.length === 0 ? (
                    <tr><td colSpan={13}><EmptyState title="No records found" description="Try adjusting your filters or search query." /></td></tr>
                  ) : paginated.map(r => (
                    <FulfillmentRecordRow
                      key={r.TransactionID}
                      record={r}
                      selected={selectedIds.has(r.TransactionID)}
                      onToggle={toggleOne}
                      onRefresh={fetchRecords}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <Pagination total={filtered.length} page={page} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} />
        </div>

      </div>
    </div>
  );
}